/**
 * PKCE (Proof Key for Code Exchange, RFC 7636) helpers.
 *
 * Pure functions so they can be unit-tested without React.
 * Uses the Web Crypto API (crypto.getRandomValues + crypto.subtle.digest)
 * which is available in all target browsers (chrome87+, safari14+, firefox80+, edge88+).
 */

/** Encode raw bytes as base64url (RFC 4648 §5) without padding. */
export function base64UrlEncode(bytes: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    // window.btoa rather than btoa: both resolve to the same Web API, but the
    // explicit window scope avoids picking up the deprecated @types/node global.
    return window
        .btoa(binary)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

/**
 * Generate a cryptographically random PKCE code_verifier.
 *
 * Per RFC 7636 §4.1 the verifier is 43–128 characters from the unreserved set
 * [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~". base64url-encoding 32 random
 * bytes yields a 43-character string within that alphabet, which is the minimum
 * length the spec recommends.
 */
export function generateCodeVerifier(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}

/**
 * Derive the PKCE code_challenge for the S256 method: base64url(SHA-256(verifier)).
 */
export async function deriveCodeChallenge(verifier: string): Promise<string> {
    if (!crypto?.subtle) {
        throw new Error(
            'Sign in requires a secure context (HTTPS or localhost). ' +
                "If you are testing inside an iframe, use the host page's " +
                '"Authenticate" button to open the login in a popup instead.'
        );
    }
    const encoded = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return base64UrlEncode(new Uint8Array(digest));
}

/** Generate an opaque random string for the OAuth `state` CSRF parameter. */
export function generateState(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
}
